import React, { useState } from 'react';
import { MapPin, Clock, Phone, Mail, Instagram } from 'lucide-react';
import contatoImg from '../../../assets/CasadoPandeiro_contato.jpeg';

const info = {
  endereco: 'Travessa do Ouvidor, 36',
  bairro: 'Centro, Rio de Janeiro – RJ, Brasil',
  cep: 'CEP: 20040-040',
  referencia: 'Próximo ao Metrô Carioca',
  horario: 'Segunda a sábado, das 10h às 17h; fechamento pode variar conforme a programação anunciada',
  whatsapp: '5521986013006',
  whatsappDisplay: '(21) 98601-3006',
  email: 'contato@casadopandeiro.com',
  instagram: 'https://instagram.com/casadopandeiro',
  instagramHandle: '@casadopandeiro',
  mapsLink: 'https://maps.google.com/?q=Travessa+do+Ouvidor+36+Centro+Rio+de+Janeiro',
};

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export const ContatoPage: React.FC = () => {
  const [form, setForm] = useState({ nome: '', email: '', mensagem: '' });
  const [gotcha, setGotcha] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (gotcha) return;

    const formId = process.env.REACT_APP_FORMSPREE_FORM_ID;
    if (!formId) {
      console.error('[Contato] REACT_APP_FORMSPREE_FORM_ID não definido no build');
      setStatus('error');
      return;
    }

    setStatus('submitting');

    try {
      const res = await fetch(`https://formspree.io/f/${formId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          nome: form.nome,
          email: form.email,
          message: form.mensagem,
          _subject: 'Contato via site - Casa do Pandeiro',
        }),
      });

      if (res.ok) {
        setStatus('success');
      } else {
        const bodyText = await res.text();
        let detail: unknown = bodyText;
        try {
          detail = JSON.parse(bodyText);
        } catch {
          // mantém como texto
        }
        console.error('[Contato] Formspree rejeitou o envio:', {
          status: res.status,
          statusText: res.statusText,
          detail,
        });
        setStatus('error');
      }
    } catch (error) {
      console.error('[Contato] Falha na requisição ao Formspree:', error);
      setStatus('error');
    }
  };

  return (
    <>
      <div
        className="w-full"
        style={{
          backgroundImage: `url(${contatoImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        role="img"
        aria-label="Casa do Pandeiro - Contato"
      >
        <div className="container py-40 md:py-56" />
      </div>

      <div className="container max-w-4xl pt-8 pb-16 mx-auto md:pt-12 md:pb-20">
        <h1 className="mb-3 text-3xl md:text-4xl text-primary">Contato</h1>
        <p className="mb-10 text-muted-foreground">Como encontrar a Casa do Pandeiro</p>

        <div className="grid gap-10 md:grid-cols-2">
          {/* Info */}
          <div className="space-y-5">
            <div className="flex gap-3">
              <MapPin className="w-5 h-5 text-accent mt-0.5 shrink-0" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground">{info.endereco}</p>
                <p>{info.bairro}</p>
                <p>{info.cep}</p>
                <p>{info.referencia}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <Clock className="w-5 h-5 text-accent mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground">{info.horario}</p>
            </div>

            <div className="flex gap-3">
              <Phone className="w-5 h-5 text-accent mt-0.5 shrink-0" />
              <a
                href={`https://wa.me/${info.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                Falar pelo WhatsApp — {info.whatsappDisplay}
              </a>
            </div>

            <div className="flex gap-3">
              <Mail className="w-5 h-5 text-accent mt-0.5 shrink-0" />
              <a href={`mailto:${info.email}`} className="text-sm text-primary hover:underline">
                {info.email}
              </a>
            </div>

            <div className="flex gap-3">
              <Instagram className="w-5 h-5 text-accent mt-0.5 shrink-0" />
              <a href={info.instagram} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                {info.instagramHandle}
              </a>
            </div>

            <a
              href={info.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-4 text-sm font-medium text-accent hover:underline"
            >
              📍 Ver no Google Maps →
            </a>
          </div>

          {/* Form */}
          <div className="p-6 border rounded-lg bg-card">
            {status === 'success' ? (
              <div className="py-8 text-center">
                <p className="mb-2 text-lg font-semibold text-primary">Mensagem enviada!</p>
                <p className="text-sm text-muted-foreground">Retornaremos o mais breve possível.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  name="_gotcha"
                  value={gotcha}
                  onChange={(e) => setGotcha(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Nome</label>
                  <input
                    type="text"
                    required
                    disabled={status === 'submitting'}
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-md border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                  <input
                    type="email"
                    required
                    disabled={status === 'submitting'}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-md border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Mensagem</label>
                  <textarea
                    required
                    rows={4}
                    disabled={status === 'submitting'}
                    value={form.mensagem}
                    onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-md border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none disabled:opacity-60"
                  />
                </div>
                {status === 'error' && (
                  <p className="text-sm text-destructive">
                    Não foi possível enviar a mensagem. Tente novamente ou escreva para{' '}
                    <a href={`mailto:${info.email}`} className="underline">
                      {info.email}
                    </a>
                    .
                  </p>
                )}
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-3 font-semibold transition-opacity rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-60"
                >
                  {status === 'submitting' ? 'Enviando...' : 'Enviar mensagem'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
